import { useState, useEffect } from "react"
import { BackButton } from "@/components/custom/BackButton"
import { PageTitle } from "@/components/custom/PageTitle"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { useLocation, useParams } from "react-router-dom"
import { Form } from "@/components/ui/form"
import { Organization } from "@/types/organizations"
import { useSelector, useDispatch } from "react-redux"
import { AppDispatch, RootState } from "@/redux"
import { useNavigate } from "react-router-dom"
import { deleteOrganization, switchOrganization, updateOrganization } from "@/services/organizationService"
import { AlertDialogButton } from "@/components/custom/AlertDialogButton/AlertDialogButton"
import { TextInputField } from "@/components/custom/InputField/TextInputField"
import { AlertDialogButtons, AlertDialogTitle } from "@/constants/alert-dialog/alert-dialog"
import { ButtonVariant } from "@/constants/button/button"
import { Separator } from "@/components/ui/separator"
import relativeTime from 'dayjs/plugin/relativeTime';
import dayjs from 'dayjs';
import { OrganizationApiKeys } from "@/components/custom/OrganizationApiKeys/OrganizationApiKey"
import { AdminBillingTools } from "@/pages/Billing/components/AdminBillingTools"
dayjs.extend(relativeTime);

const formSchema = z.object({
  name: z.string().min(3, {
    message: 'Organization name must be at least 3 characters.'
  })
})

export const EditOrganization = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { id: orgId } = useParams()
  const dispatch: AppDispatch = useDispatch()
  const { token, userData } = useSelector((state: RootState) => state.user)
  const { organizations } = useSelector((state: RootState) => state.organization)
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | undefined>(undefined)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: selectedOrganization?.name ?? "",
    },
  })

  const onUpdateOrganization = async (
    values: z.infer<typeof formSchema>
  ) => {
    if (!token || !selectedOrganization?.id) return
    const response = await dispatch(updateOrganization(token, values, selectedOrganization.id))
    if (response.data) {
      navigate('/organizations')
    }
  }

  const onSwitchOrganization = async () => {
    if (!token || !selectedOrganization?.id) return
    const response = await dispatch(switchOrganization(token, selectedOrganization.id))
    if (response.data) {
      navigate('/organizations')
    }
  }

  const onDeleteOrganization = async () => {
    if (!token || !selectedOrganization?.id) return
    const response = await dispatch(deleteOrganization(token, selectedOrganization.id))
    if (response.data) {
      navigate('/organizations')
    }
  }

  useEffect(() => {
    const orgId = location.pathname.split('/organizations/edit/')[1]
    if (orgId && organizations.length) {
      const org = organizations.find(o => o.id === orgId)
      if (org) {
        setSelectedOrganization(org)
        form.setValue('name', org.name)
      } else {
        navigate('/organizations')
      }
    }
  }, [location.pathname, organizations, navigate, form])

  return (
    <div className="protectedPageLayout">
      <BackButton buttonLink="/organizations" buttonText="Back to Organizations" />
      <PageTitle title="Edit Organization" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onUpdateOrganization)}
          className="space-y-8 flex flex-col items-start"
        >
          <TextInputField
            form={form}
            schema={formSchema}
            name="name"
            label="Organization Name"
            error={!!form.formState.errors.name}
            placeholder="XYZ"
            required={true}
          />
          <div className="flex flex-row items-center justify-start">
            <Button
              type="submit"
              className="mb-2 font-semibold mr-6"
              disabled={form.getValues('name') === selectedOrganization?.name}
            >
              Update
            </Button>
            {selectedOrganization?.id !== userData?.currentOrganization?.id ? (
              <Button
                type="button"
                className="mb-2 font-semibold mr-6"
                variant={ButtonVariant.OUTLINE}
                onClick={onSwitchOrganization}
              >
                Login As
              </Button>
            ) : null}
            <AlertDialogButton
              actionButtonText={AlertDialogButtons.DELETE}
              dialogTitle={AlertDialogTitle.DELETE_ORGANIZATION}
              dialogSubtitle={`You are about to permanently delete "${form.getValues('name')}",
                    including all of it's credentials, workflows and users.`}
              onClick={onDeleteOrganization}
              variant={ButtonVariant.LINK}
              actionButtonVariant={ButtonVariant.DESTRUCTIVE}
              isTableActionButton={false}
            />
          </div>
        </form>
      </Form>
      {orgId ? (
        <>
          <Separator className="mt-6 w-[400px]" />
          <OrganizationApiKeys orgId={orgId} />

          {/* Admin Billing Tools - Only visible to TCS Admins */}
          {userData?.isTCSAdmin && token && (
            <>
              <Separator className="mt-6 w-[400px]" />
              <AdminBillingTools
                token={token}
                organizationId={orgId}
                onUpdate={() => {
                  // Refresh any billing data if needed
                  console.log('Billing tools action completed')
                }}
              />
            </>
          )}
        </>
      ) : null}
    </div>
  )
}
